import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { SessionStorageKeys } from '../../SessionStorageKeys';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { SessionService } from '../session/session.service';

declare const window: Window &
  typeof globalThis & {
    duid: any;
  };

@Injectable({
  providedIn: 'root',
})
export class DeviceHandlerService {
  private _deviceId: string;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(private readonly session: SessionService, private localStorage: LocalStorageService) {}

  public getCurrentDeviceID(): string {
    if (!this.localStorage.isDefined()) {
      if (!this._deviceId) {
        this._deviceId = this.generateDuid();
      }
      return this._deviceId;
    }
    if (this._deviceId) {
      this.localStorage.setItem(LocalStorageKeys.DEVICEREF, this._deviceId);
    }
    let id = this.localStorage.getItem(LocalStorageKeys.DEVICEREF);

    // Avoids creating a new deviceID for existing Session ID
    const sessionNotDefined = this.session.getSessionID() === undefined;

    // If session doesn't exist, can create new device ID
    if (sessionNotDefined) {
      // generate a new DUID if id is null or empty
      while (id === null || (id !== null && id.trim().length === 0)) {
        id = this.generateDuid();
        this.localStorage.setItem(LocalStorageKeys.DEVICEREF, id);
      }
    } else {
      if (
        id === null ||
        (id !== null && id?.trim().length === 0) ||
        id === undefined ||
        id?.trim().toUpperCase() === 'NULL' ||
        id?.trim().toUpperCase() === 'UNDEFINED'
      ) {
        // This case should not happen, as the session is defined, therefore there must be a deviceref already in localStorage
        id = this.generateDuid();
        this.localStorage.setItem(LocalStorageKeys.DEVICEREF, id);
        const newSessionId = this.generateDuid();
        sessionStorage.setItem(SessionStorageKeys.SESSIONID, newSessionId.replace(/-/g, ''));
      }
    }

    return id;
  }

  public getXDeviceInfo(): string {
    const jsonString = JSON.stringify(this.getDeviceInfo());
    const base64String = btoa(jsonString);

    return base64String;
  }

  public getXDeviceFingerprint(devId: string): string {
    return btoa(devId);
  }

  /**
   * Get the device uid if the device is a tizen device, otherwise generate a uuid
   *
   * @returns device uid
   */
  generateDuid(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    const isSmartTV = userAgent.includes('tizen') || userAgent.includes('web0s') || userAgent.includes('vizio');
    // Validar si el useragent contiene la palabra 'web0s' o 'tizen'
    if (isSmartTV && window.duid) {
      const duid = window.duid;
      return duid;
    }
    return uuid();
  }

  getUserAgentDetails() {
    const userAgent = navigator.userAgent;
    let userAgentName = 'Unknown';
    let userAgentVersion = 'Unknown';
    let userAgentOS = 'Unknown';

    // Detect Browser Name & Version
    if (/Safari\/(\d+\.\d+)/.test(userAgent) && /AppleWebKit/.test(userAgent)) {
      userAgentName = 'Safari (WPE WebKit)';
      userAgentVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
    }

    // Detect OS
    if (/Linux/.test(userAgent)) {
      userAgentOS = 'Linux';
    }

    return { userAgentName, userAgentVersion, userAgentOS };
  }

  private getDeviceInfo() {
    let deviceInfo = '';

    // Get User-Agent string
    const userAgent = navigator.userAgent;
    deviceInfo += `User-Agent: ${userAgent}\n`;

    // Get screen resolution
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    deviceInfo += `Screen Resolution: ${screenResolution}\n`;

    // Get device type (Desktop/Mobile)
    const isMobile = /Mobi|Android/i.test(userAgent);
    deviceInfo += `Device Type: ${isMobile ? 'Mobile' : 'Desktop'}\n`;

    return deviceInfo;
  }
}
